import { makeStyles } from "tss-react/mui"
import { Box, Skeleton, Typography } from "@mui/material"
import { DateTimeFactory } from "../../../../modules/localization/useDateTime"
import { LocalizedEnum } from "../../../../modules/localization/components/AppLocalized"
import {
    MessageTagIcon,
    notificationOptions,
} from "../../../../modules/merchant/admin/messages/components/MessageTagAutoComplete"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import type { Notification } from "./notificationApi"

const useStyles = makeStyles()((theme) => ({
    menuItem: {
        padding: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: ".5rem",
        "&:hover": {
            backgroundColor: "white",
        },
    },
    innerCard: {
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
        width: "100%",
    },
    subject: {
        fontWeight: 500,
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    messageTime: {
        alignItems: "center",
        display: "flex",
        gap: "0.75rem",
    },
    colorBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "100%",
        color: theme.palette.common.white,
        "& > svg": {
            fontSize: ".75rem",
        },
    },
}))

type NotificationCardProps = Readonly<{
    notification: Notification | undefined
    dateTimeFactory: DateTimeFactory
}>

export function NotificationCard({ notification, dateTimeFactory }: NotificationCardProps) {
    const { classes } = useStyles()

    if (!notification) {
        return <Skeleton variant="rounded" animation="wave" width={410} height={100} />
    }

    const time = dateTimeFactory.fromApi(notification.created)
    const selectedOption = notificationOptions.find((opt) => opt.value === notification.tag) ?? null

    return (
        <Box className={classes.menuItem}>
            <Box className={classes.innerCard}>
                <Box className={classes.cardHeader}>
                    {selectedOption && (
                        <MessageTagIcon
                            color={selectedOption.color}
                            icon={selectedOption.icon}
                            className={classes.colorBox}
                        />
                    )}
                    <Typography variant="body1" className={classes.subject}>
                        {notification.subject}
                    </Typography>
                </Box>
                <Typography variant="body1" whiteSpace="normal">
                    {notification.message}
                </Typography>
                <Box className={classes.messageTime}>
                    {notification.tag && (
                        <>
                            <LocalizedEnum
                                base="user-home-top-bar-news-notification-time-type"
                                enumValue={notification.tag}
                            >
                                <Typography color="textSecondary" variant="caption">
                                    Nyhed
                                </Typography>
                            </LocalizedEnum>
                            <FontAwesomeIcon icon={faCircle} color="#94A3B8" width="5px" />
                        </>
                    )}
                    <Typography variant="caption" color="textSecondary">
                        {dateTimeFactory.formatShortRelative(time)}
                    </Typography>
                    {notification.sender?.type && (
                        <>
                            <FontAwesomeIcon icon={faCircle} color="#94A3B8" width="5px" />
                            <LocalizedEnum
                                base="user-home-top-bar-news-notification-sender-type"
                                enumValue={notification.sender.type}
                            >
                                <Typography variant="caption" color="textSecondary">
                                    Merchant
                                </Typography>
                            </LocalizedEnum>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
